import { stepStudentMedicalBenefitsTugoLookup as expenseLookup } from './claimCodeMapper';
import { MEDICAL_EXPENSE_TYPES } from '../Tugo.StudentMedicalBenefits/constants';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire() {
  return [];
}

// Generic function to map any type of expense
function mapExpensesByType(data, itemsKey, expenseLookupKey, condition = true) {
  if (!condition || !data[itemsKey] || data[itemsKey].length < 1) {
    return [];
  }

  if (!Object.prototype.hasOwnProperty.call(expenseLookup, expenseLookupKey)) {
    return [];
  }

  // Get claim code from associatedClaimCodes
  const lookup = expenseLookup[expenseLookupKey];
  const claimCode = lookup && lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0
    ? lookup.associatedClaimCodes[0]
    : (lookup.claimCode || null);
  
  if (!claimCode) {
    return [];
  }

  const expenses = [];

  data[itemsKey].forEach((item) => {
    const expense = mapExpenseItem(item, data, claimCode);
    if (!!expense) {
      expenses.push(expense);
    }
  });

  return expenses;
}

function mapToExpenses(data) {
  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];
  const hasExpenseItems = (items) => {
    if (!items) return false;
    const itemCount = items.size !== undefined ? items.size : items.length;
    return itemCount > 0;
  };
  const isReturnOfVehicleSelected = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.RETURN_OF_VEHICLE);
  const isReturnOfVehicleCommercialAgencySelected = isReturnOfVehicleSelected
    && (data.returnOfVehicleCommercialAgency === true || hasExpenseItems(data.infoReturnOfVehicleCommercialAgencyItems));
  const isReturnOfVehicleFamilyMemberFriendSelected = isReturnOfVehicleSelected
    && (data.returnOfVehicleFamilyMemberFriend === true || hasExpenseItems(data.infoReturnOfVehicleFamilyMemberFriendItems));

  const expenseConfigs = [
    // Doctor's Visit expenses
    {
      items: 'infoTelemedicineItems',
      lookup: 'telemedicine',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitTelemedicine === 'true',
    },
    {
      items: 'infoAnnualPhysicalExamItems',
      lookup: 'annualPhysicalExam',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitAnnualPhysicalExam === 'true',
    },
    {
      items: 'infoOutpatientPhysicianFeesItems',
      lookup: 'outpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true',
    },
    {
      items: 'infoFollowUpVisitItems',
      lookup: 'followUpVisit',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
    },
    {
      items: 'infoFollowUpFractureItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true',
    },
    {
      items: 'infoSTITestingItems',
      lookup: 'stiTesting',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitSTITesting === true,
    },
    {
      items: 'infoVaccinationsTBTestingItems',
      lookup: 'vaccinationsTBTesting',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitVaccinationsTBTesting === true,
    },
    {
      items: 'infoEyeExaminationItems',
      lookup: 'eyeExamination',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitEyeExamination === true,
    },

    // Diagnostic Tests expenses
    {
      items: 'infoDiagnosticTestsItems',
      lookup: 'diagnosticTests',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS),
    },

    // Prescription Drugs expenses
    {
      items: 'infoPrescriptionDrugsItems',
      lookup: 'prescriptionDrugs',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
    },

    // Ambulance Services expenses
    {
      items: 'infoGroundAmbulanceItems',
      lookup: 'groundAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceGroundService === 'true',
    },
    {
      items: 'infoAirAmbulanceItems',
      lookup: 'airAmbulance',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'true',
    },
    {
      items: 'infoGroundAmbulanceItems',
      lookup: 'ambulanceServices',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) && data.ambulanceAirService === 'false',
    },

    // Hospitalization expenses
    {
      items: 'infoHospitalFeesItems',
      lookup: 'hospitalFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalInvoice === true,
    },
    {
      items: 'infoInpatientPhysicianFeesItems',
      lookup: 'inpatientPhysicianFees',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === true,
    },
    {
      items: 'infoXrayLabsDiagnosticsItems',
      lookup: 'xrayLabsDiagnostics',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === true,
    },
    {
      items: 'infoOtherHospitalizationItems',
      lookup: 'otherHospitalization',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === true,
    },
    {
      items: 'infoMentalHealthPsychiatricItems',
      lookup: 'mentalHealthPsychiatric',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMentalHealthPsychiatric === true,
    },
    {
      items: 'infoEmergencyAirTransportationItems',
      lookup: 'emergencyAirTransportation',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationEmergencyAirTransportation === true,
    },
    {
      items: 'infoAirfareReturnHomeItems',
      lookup: 'airfareReturnHome',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationAirfareReturnHome === true,
    },
    {
      items: 'infoFamilyTransportationItems',
      lookup: 'familyTransportation',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportation === true,
    },
    {
      items: 'infoMedicalAttendantItems',
      lookup: 'medicalAttendant',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMedicalAttendant === true,
    },

    // Essential Medical Appliances expenses
    {
      items: 'infoEssentialMedicalAppliancesItems',
      lookup: 'essentialMedicalAppliances',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
    },

    // Return of Vehicle expenses
    {
      items: 'infoReturnOfVehicleCommercialAgencyItems',
      lookup: 'returnOfVehicleCommercialAgency',
      condition: isReturnOfVehicleCommercialAgencySelected,
    },
    {
      items: 'infoReturnOfVehicleFamilyMemberFriendItems',
      lookup: 'returnOfVehicleFamilyMemberFriend',
      condition: isReturnOfVehicleFamilyMemberFriendSelected,
    },

    // Private Duty Nursing expenses
    {
      items: 'infoPrivateDutyNursingItems',
      lookup: 'privateDutyNursing',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
    },

    // Fracture Treatment expenses
    {
      items: 'infoFractureTreatmentItems',
      lookup: 'fractureTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT),
    },

    // Maternity Benefits expenses
    {
      items: 'infoRoutinePreNatalCareItems',
      lookup: 'routinePreNatalCare',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsRoutinePreNatalCare === true,
    },
    {
      items: 'infoComplicationsOfPregnancyItems',
      lookup: 'complicationsOfPregnancy',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsComplicationsOfPregnancy === true,
    },
    {
      items: 'infoInvoluntaryTerminationItems',
      lookup: 'involuntaryTermination',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsInvoluntaryTermination === true,
    },

    // Professional Services expenses
    {
      items: 'infoPhysiotherapistItems',
      lookup: 'physiotherapist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPhysiotherapist === true,
    },
    {
      items: 'infoChiropractorItems',
      lookup: 'chiropractor',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropractor === true,
    },
    {
      items: 'infoChiropodistItems',
      lookup: 'chiropodist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropodist === true,
    },
    {
      items: 'infoOsteopathItems',
      lookup: 'osteopath',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesOsteopath === true,
    },
    {
      items: 'infoPodiatristItems',
      lookup: 'podiatrist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPodiatrist === true,
    },
    {
      items: 'infoAcupuncturistItems',
      lookup: 'acupuncturist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesAcupuncturist === true,
    },
    {
      items: 'infoNaturopathItems',
      lookup: 'naturopath',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesNaturopath === true,
    },
    {
      items: 'infoSpeechTherapistItems',
      lookup: 'speechTherapist',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesSpeechTherapist === true,
    },
    {
      items: 'infoMentalHealthOrSupportPractitionerItems',
      lookup: 'mentalHealthOrSupportPractitioner',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesMentalHealthOrSupportPractitioner === true,
    },

    // Rehabilitation Treatment expenses
    {
      items: 'infoRehabilitationTreatmentItems',
      lookup: 'rehabilitationTreatment',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.REHABILITATION_TREATMENT),
    },

    // Dental Services expenses
    {
      items: 'infoDentalAccidentItems',
      lookup: 'dentalAccident',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalAccident === true,
    },
    {
      items: 'infoImpactedWisdomToothItems',
      lookup: 'impactedWisdomTooth',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesImpactedWisdomTooth === true,
    },
    {
      items: 'infoDentalPainOtherItems',
      lookup: 'dentalPainOther',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalPainOther === true,
    },

    // Prescription Glasses, Contact Lenses or Hearing Aids expenses
    {
      items: 'infoPrescriptionGlassesContactLensesHearingAidsItems',
      lookup: 'prescriptionGlassesContactLensesHearingAids',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_GLASSES_CONTACT_LENSES_HEARING_AIDS),
    },

    // Tutorial Services expenses
    {
      items: 'infoTutorialServicesItems',
      lookup: 'tutorialServices',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.TUTORIAL_SERVICES),
    },
    // Other (unrelated to any benefit listed above)
    {
      items: 'infoOtherUnrelatedBenefitItems',
      lookup: 'otherUnrelatedBenefit',
      condition: expensesSelected.includes(MEDICAL_EXPENSE_TYPES.OTHER_UNRELATED_BENEFIT),
    },
  ];
  // Map all expenses using the generic function
  const allExpenses = expenseConfigs.flatMap((config) =>
    mapExpensesByType(data, config.items, config.lookup, config.condition)
  );

  return [...allExpenses];
}

function mapToClaimExpensesLevel(data, claimTypes, selectedBenefits) {
  const selected =
    !!selectedBenefits && typeof selectedBenefits.toJS === 'function'
      ? selectedBenefits.toJS()
      : selectedBenefits || [];
  const claimCodeList = expenseLookup.claimCodeList;
  const list = [];
  if (selected.length > 0 && !selected.some((sel) => claimCodeList.includes(sel.claimCode))) {
    return list;
  }

  const expensesSelected = !!data.whatExpensesAreYouClaimingFor && typeof data.whatExpensesAreYouClaimingFor.toJS === 'function'
    ? data.whatExpensesAreYouClaimingFor.toJS()
    : data.whatExpensesAreYouClaimingFor || [];
  const hasExpenseItems = (items) => {
    if (!items) return false;
    const itemCount = items.size !== undefined ? items.size : items.length;
    return itemCount > 0;
  };
  const isReturnOfVehicleSelected = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.RETURN_OF_VEHICLE);
  const isReturnOfVehicleCommercialAgencySelected = isReturnOfVehicleSelected
    && (data.returnOfVehicleCommercialAgency === true || hasExpenseItems(data.infoReturnOfVehicleCommercialAgencyItems));
  const isReturnOfVehicleFamilyMemberFriendSelected = isReturnOfVehicleSelected
    && (data.returnOfVehicleFamilyMemberFriend === true || hasExpenseItems(data.infoReturnOfVehicleFamilyMemberFriendItems));

  if (expensesSelected.length < 1 || claimTypes === undefined) {
    return list;
  }

  const medicalExpenses = mapToExpenses(data);
  
  const getClaimCodeFromLookup = (lookupKey) => {
    const lookup = expenseLookup[lookupKey];
    return lookup && lookup.associatedClaimCodes && lookup.associatedClaimCodes.length > 0
      ? lookup.associatedClaimCodes[0]
      : null;
  };

  // Create mapping configuration by lookup keys (not hard-coded claim codes)
  // Note: Documents should ONLY be shown when specific questions are answered "Yes"
  // NOT when only the checkbox is selected
  const lookupKeyToConfig = {
    // Doctor's Visit - Telemedicine (telephone or video call)
    telemedicine: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitTelemedicine === 'true',
      getDescription: () => 'Telemedicine Benefit',
    },
    // Doctor's Visit - Annual Physical Visit
    annualPhysicalExam: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitAnnualPhysicalExam === 'true',
      getDescription: () => 'Annual Physical Visit',
    },
    // Doctor's Visit - First Visit
    outpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT, MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFirstVisit === 'true',
      getDescription: () => 'Outpatient Physician Fees',
      documentClaimCodeLookupKey: 'physicianParent',
      getDocumentDescription: () => 'Physician fees',
    },
    // Doctor's Visit - Follow-up Visit
    followUpVisit: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUp === 'true',
      getDescription: () => 'Follow-up visits',
    },
    // Fracture Treatment - Follow-up Fracture
    fractureTreatment: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT, MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT],
      condition: () => {
        const isDoctorVisitFracture = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitFollowUpFracture === 'true';
        const isStandaloneFracture = expensesSelected.includes(MEDICAL_EXPENSE_TYPES.FRACTURE_TREATMENT);
        return isDoctorVisitFracture || isStandaloneFracture;
      },
      getDescription: () => 'Fracture treatment',
    },
    // Doctor's Visit - STI Testing
    stiTesting: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitSTITesting === true,
      getDescription: () => 'Sexually Transmitted Infection Testing',
    },
    // Doctor's Visit - Vaccinations and Tuberculosis Testing
    vaccinationsTBTesting: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitVaccinationsTBTesting === true,
      getDescription: () => 'Vaccinations and Tuberculosis Testing',
    },
    // Doctor's Visit - Eye Examination
    eyeExamination: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DOCTORS_VISIT) && data.doctorVisitSTIVaccinationsTBEyeExam === 'true' && data.doctorVisitEyeExamination === true,
      getDescription: () => 'Eye Examination',
    },
    // Diagnostic Tests
    diagnosticTests: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS],
      condition: (claimCode) => {
        // Check if this is from Diagnostic Tests checkbox only
        // Note: hospitalizationLabDiagnostic is handled separately by xrayLabsDiagnostics lookup
        // Note: hospitalizationOtherRelated is handled separately by otherHospitalization lookup
        return expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS);
      },
      getDescription: () => {
        return MEDICAL_EXPENSE_TYPES.DIAGNOSTIC_TESTS;
      },
    },
    // Prescription Drugs
    prescriptionDrugs: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS),
      getDescription: () => MEDICAL_EXPENSE_TYPES.PRESCRIPTION_DRUGS,
    },
    // Ambulance Services - Ground
    groundAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () =>
        expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) &&
        (data.ambulanceGroundService === 'true' || data.ambulanceAirService === 'false'),
      getDescription: () => 'Ambulance services',
      documentClaimCodeLookupKey: 'ambulanceParent',
      getDocumentDescription: () => 'Ambulance services',
    },
    // Ambulance Services - Air
    airAmbulance: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES],
      condition: () =>
        expensesSelected.includes(MEDICAL_EXPENSE_TYPES.AMBULANCE_SERVICES) &&
        data.ambulanceAirService === 'true',
      getDescription: () => 'Emergency Air Transportation',
      documentClaimCodeLookupKey: 'ambulanceParent',
      getDocumentDescription: () => 'Ambulance services',
    },
    // Hospitalization - Hospital Fees
    hospitalFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationHospitalInvoice === true,
      getDescription: () => 'Hospital fees',
    },
    // Hospitalization - Inpatient Physician Fees
    inpatientPhysicianFees: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationPhysicianInvoice === true,
      getDescription: () => 'Physician fees',
      documentClaimCodeLookupKey: 'physicianParent',
      getDocumentDescription: () => 'Physician fees',
    },
    // Hospitalization - X-ray/Labs/Diagnostics (uses same VTC-DIAG as diagnosticTests)
    xrayLabsDiagnostics: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationLabDiagnostic === true,
      getDescription: () => 'Hospital services',
    },
    // Hospitalization - Other Related
    otherHospitalization: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationOtherRelated === true,
      getDescription: () => 'Other expenses related to hospitalization',
    },
    // Hospitalization - Mental health or psychiatric hospitalization
    mentalHealthPsychiatric: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMentalHealthPsychiatric === true,
      getDescription: () => 'Mental health or psychiatric hospitalization',
    },
    // Hospitalization - Emergency Air Transportation
    emergencyAirTransportation: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationEmergencyAirTransportation === true,
      getDescription: () => 'Emergency Air Transportation',
    },
    // Hospitalization - Airfare to Return Home For Treatment Benefit
    airfareReturnHome: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationAirfareReturnHome === true,
      getDescription: () => 'Airfare to Return Home For Treatment Benefit',
    },
    // Hospitalization - Family Transportation
    familyTransportation: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationFamilyTransportation === true,
      getDescription: () => 'Family Transportation',
    },
    // Hospitalization - Medical Attendant
    medicalAttendant: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.HOSPITALIZATION],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && data.hospitalizationMedicalAttendant === true,
      getDescription: () => 'Medical Attendant',
    },
    // Essential Medical Appliances
    essentialMedicalAppliances: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES),
      getDescription: () => MEDICAL_EXPENSE_TYPES.ESSENTIAL_MEDICAL_APPLIANCES,
    },
    // Return of Vehicle - Commercial agency
    returnOfVehicleCommercialAgency: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.RETURN_OF_VEHICLE],
      condition: () => isReturnOfVehicleCommercialAgencySelected,
      getDescription: () => 'Return of Vehicle',
    },
    // Return of Vehicle - Family member or friend
    returnOfVehicleFamilyMemberFriend: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.RETURN_OF_VEHICLE],
      condition: () => isReturnOfVehicleFamilyMemberFriendSelected,
      getDescription: () => 'Return of Vehicle',
    },
    // Private Duty Nursing
    privateDutyNursing: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING),
      getDescription: () => MEDICAL_EXPENSE_TYPES.PRIVATE_DUTY_NURSING,
    },
    // Maternity Benefits - share one document section (documentClaimCodeLookupKey)
    routinePreNatalCare: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsRoutinePreNatalCare === true,
      getDescription: () => 'Routine Pre-Natal Care',
      documentClaimCodeLookupKey: 'maternityBenefits',
      getDocumentDescription: () => 'Maternity Benefits',
    },
    complicationsOfPregnancy: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsComplicationsOfPregnancy === true,
      getDescription: () => 'Complications of Pregnancy',
      documentClaimCodeLookupKey: 'maternityBenefits',
      getDocumentDescription: () => 'Maternity Benefits',
    },
    involuntaryTermination: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.MATERNITY_BENEFITS) && data.maternityBenefitsInvoluntaryTermination === true,
      getDescription: () => 'Involuntary termination',
      documentClaimCodeLookupKey: 'maternityBenefits',
      getDocumentDescription: () => 'Maternity Benefits',
    },
    // Professional Services - Physiotherapist
    physiotherapist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPhysiotherapist === true,
      getDescription: () => 'Physiotherapist',
    },
    // Professional Services - Chiropractor
    chiropractor: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropractor === true,
      getDescription: () => 'Chiropractor',
    },
    // Professional Services - Chiropodist
    chiropodist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesChiropodist === true,
      getDescription: () => 'Chiropodist',
    },
    // Professional Services - Osteopath
    osteopath: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesOsteopath === true,
      getDescription: () => 'Osteopath',
    },
    // Professional Services - Podiatrist
    podiatrist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesPodiatrist === true,
      getDescription: () => 'Podiatrist',
    },
    // Professional Services - Acupuncturist
    acupuncturist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesAcupuncturist === true,
      getDescription: () => 'Acupuncturist',
    },
    // Professional Services - Naturopath
    naturopath: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesNaturopath === true,
      getDescription: () => 'Naturopath',
    },
    // Professional Services - Speech Therapist
    speechTherapist: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesSpeechTherapist === true,
      getDescription: () => 'Speech Therapist',
    },
    // Professional Services - Mental Health or Support Practitioner
    mentalHealthOrSupportPractitioner: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PROFESSIONAL_SERVICES) && data.professionalServicesMentalHealthOrSupportPractitioner === true,
      getDescription: () => 'Mental Health or Support Practitioner',
    },
    // Rehabilitation Treatment
    rehabilitationTreatment: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.REHABILITATION_TREATMENT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.REHABILITATION_TREATMENT),
      getDescription: () => 'Rehabilitation Treatment',
    },
    // Dental Services - Dental Accident
    dentalAccident: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalAccident === true,
      getDescription: () => 'Dental Accident (result of a blow to the face)',
    },
    // Dental Services - Impacted Wisdom Tooth
    impactedWisdomTooth: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesImpactedWisdomTooth === true,
      getDescription: () => 'Impacted Wisdom Tooth',
    },
    // Dental Services - Dental pain other than accident or wisdom tooth
    dentalPainOther: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.DENTAL_SERVICES) && data.dentalServicesDentalPainOther === true,
      getDescription: () => 'Dental pain other than accident or wisdom tooth',
    },
    // Prescription Glasses, Contact Lenses or Hearing Aids
    prescriptionGlassesContactLensesHearingAids: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.PRESCRIPTION_GLASSES_CONTACT_LENSES_HEARING_AIDS],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.PRESCRIPTION_GLASSES_CONTACT_LENSES_HEARING_AIDS),
      getDescription: () => 'Prescription Glasses, Contact Lenses or Hearing Aids',
    },
    // Tutorial Services
    tutorialServices: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.TUTORIAL_SERVICES],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.TUTORIAL_SERVICES),
      getDescription: () => 'Tutorial Services',
    },
    otherUnrelatedBenefit: {
      expenseTypes: [MEDICAL_EXPENSE_TYPES.OTHER_UNRELATED_BENEFIT],
      condition: () => expensesSelected.includes(MEDICAL_EXPENSE_TYPES.OTHER_UNRELATED_BENEFIT),
      getDescription: () => MEDICAL_EXPENSE_TYPES.OTHER_UNRELATED_BENEFIT,
    },
  };

  // Build claimCodeToExpenseTypes dynamically from lookup keys
  // First pass: collect all individual configs for each claim code
  const claimCodeToIndividualConfigs = {};
  Object.keys(lookupKeyToConfig).forEach((lookupKey) => {
    const claimCode = getClaimCodeFromLookup(lookupKey);
    if (claimCode) {
      if (!claimCodeToIndividualConfigs[claimCode]) {
        claimCodeToIndividualConfigs[claimCode] = [];
      }
      claimCodeToIndividualConfigs[claimCode].push({
        condition: lookupKeyToConfig[lookupKey].condition,
        getDescription: lookupKeyToConfig[lookupKey].getDescription,
        expenseTypes: lookupKeyToConfig[lookupKey].expenseTypes,
      });
    }
  });

  // Merge configs and create smart description function
  const claimCodeToExpenseTypes = {};
  Object.keys(claimCodeToIndividualConfigs).forEach((claimCode) => {
    const configs = claimCodeToIndividualConfigs[claimCode];
    const allExpenseTypes = configs.flatMap((config) => config.expenseTypes);
    const mergedExpenseTypes = [...new Set(allExpenseTypes)];
    
    const mergedCondition = (claimCodeParam) => {
      return configs.some((config) => config.condition(claimCodeParam));
    };
    
    const getDescription = () => {
      // Special handling for VTC-DIAG: prioritize xrayLabsDiagnostics over otherHospitalization
      if (claimCode === 'VTC-DIAG') {
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && 
            data.hospitalizationLabDiagnostic === true) {
          return 'Diagnostic Tests';
        }
        if (expensesSelected.includes(MEDICAL_EXPENSE_TYPES.HOSPITALIZATION) && 
            data.hospitalizationOtherRelated === true) {
          return 'Other expenses related to hospitalization';
        }
      }
      
      // Check conditions in reverse order (most specific/last added first)
      const reversedConfigs = [...configs].reverse();
      for (const config of reversedConfigs) {
        if (config.condition && config.condition()) {
          return config.getDescription ? config.getDescription() : null;
        }
      }
      return configs[0].getDescription ? configs[0].getDescription() : null;
    };
    
    claimCodeToExpenseTypes[claimCode] = {
      expenseTypes: mergedExpenseTypes,
      condition: mergedCondition,
      getDescription,
    };
  });

  // Map each claim type to its corresponding expenses
  // For cases where multiple lookup keys map to the same claim code (e.g., hospitalFees and xrayLabsDiagnostics both map to TS-HOSP),
  // we need to create separate sections for each lookup key that has a matching condition
  // Build lookup key to itemsKey mapping from expenseConfigs in mapToExpenses
  // This ensures we have the correct mapping for all lookup keys
  const expenseConfigsForMapping = [
    { items: 'infoTelemedicineItems', lookup: 'telemedicine' },
    { items: 'infoAnnualPhysicalExamItems', lookup: 'annualPhysicalExam' },
    { items: 'infoOutpatientPhysicianFeesItems', lookup: 'outpatientPhysicianFees' },
    { items: 'infoFollowUpVisitItems', lookup: 'followUpVisit' },
    { items: 'infoFollowUpFractureItems', lookup: 'fractureTreatment' },
    { items: 'infoSTITestingItems', lookup: 'stiTesting' },
    { items: 'infoVaccinationsTBTestingItems', lookup: 'vaccinationsTBTesting' },
    { items: 'infoEyeExaminationItems', lookup: 'eyeExamination' },
    { items: 'infoDiagnosticTestsItems', lookup: 'diagnosticTests' },
    { items: 'infoPrescriptionDrugsItems', lookup: 'prescriptionDrugs' },
    { items: 'infoGroundAmbulanceItems', lookup: 'groundAmbulance' },
    { items: 'infoAirAmbulanceItems', lookup: 'airAmbulance' },
    { items: 'infoHospitalFeesItems', lookup: 'hospitalFees' },
    { items: 'infoInpatientPhysicianFeesItems', lookup: 'inpatientPhysicianFees' },
    { items: 'infoXrayLabsDiagnosticsItems', lookup: 'xrayLabsDiagnostics' },
    { items: 'infoOtherHospitalizationItems', lookup: 'otherHospitalization' },
    { items: 'infoMentalHealthPsychiatricItems', lookup: 'mentalHealthPsychiatric' },
    { items: 'infoEmergencyAirTransportationItems', lookup: 'emergencyAirTransportation' },
    { items: 'infoAirfareReturnHomeItems', lookup: 'airfareReturnHome' },
    { items: 'infoFamilyTransportationItems', lookup: 'familyTransportation' },
    { items: 'infoMedicalAttendantItems', lookup: 'medicalAttendant' },
    { items: 'infoEssentialMedicalAppliancesItems', lookup: 'essentialMedicalAppliances' },
    { items: 'infoReturnOfVehicleCommercialAgencyItems', lookup: 'returnOfVehicleCommercialAgency' },
    { items: 'infoReturnOfVehicleFamilyMemberFriendItems', lookup: 'returnOfVehicleFamilyMemberFriend' },
    { items: 'infoPrivateDutyNursingItems', lookup: 'privateDutyNursing' },
    { items: 'infoFractureTreatmentItems', lookup: 'fractureTreatment' },
    { items: 'infoRoutinePreNatalCareItems', lookup: 'routinePreNatalCare' },
    { items: 'infoComplicationsOfPregnancyItems', lookup: 'complicationsOfPregnancy' },
    { items: 'infoInvoluntaryTerminationItems', lookup: 'involuntaryTermination' },
    { items: 'infoPhysiotherapistItems', lookup: 'physiotherapist' },
    { items: 'infoChiropractorItems', lookup: 'chiropractor' },
    { items: 'infoChiropodistItems', lookup: 'chiropodist' },
    { items: 'infoOsteopathItems', lookup: 'osteopath' },
    { items: 'infoPodiatristItems', lookup: 'podiatrist' },
    { items: 'infoAcupuncturistItems', lookup: 'acupuncturist' },
    { items: 'infoNaturopathItems', lookup: 'naturopath' },
    { items: 'infoSpeechTherapistItems', lookup: 'speechTherapist' },
    { items: 'infoMentalHealthOrSupportPractitionerItems', lookup: 'mentalHealthOrSupportPractitioner' },
    { items: 'infoRehabilitationTreatmentItems', lookup: 'rehabilitationTreatment' },
    { items: 'infoDentalAccidentItems', lookup: 'dentalAccident' },
    { items: 'infoImpactedWisdomToothItems', lookup: 'impactedWisdomTooth' },
    { items: 'infoDentalPainOtherItems', lookup: 'dentalPainOther' },
    { items: 'infoPrescriptionGlassesContactLensesHearingAidsItems', lookup: 'prescriptionGlassesContactLensesHearingAids' },
    { items: 'infoTutorialServicesItems', lookup: 'tutorialServices' },
    { items: 'infoOtherUnrelatedBenefitItems', lookup: 'otherUnrelatedBenefit' },
  ];
  
  const lookupKeyToItemsKey = {};
  expenseConfigsForMapping.forEach((config) => {
    lookupKeyToItemsKey[config.lookup] = config.items;
  });

  // Group by document claim code (so e.g. maternity sub-benefits share one section)
  const documentSectionsByClaimCode = new Map(); // documentClaimCode -> { description, expenses, lookupKeys }

  Object.keys(lookupKeyToConfig).forEach((lookupKey) => {
    const claimCode = getClaimCodeFromLookup(lookupKey);
    const config = lookupKeyToConfig[lookupKey];
    const documentClaimCode = config.documentClaimCodeLookupKey
      ? getClaimCodeFromLookup(config.documentClaimCodeLookupKey)
      : claimCode;
    if (!documentClaimCode) return;
    if (!config.condition || !config.condition(claimCode)) return;

    const matchingExpenseType = config.expenseTypes.find((expenseType) =>
      expensesSelected.includes(expenseType)
    );
    if (!matchingExpenseType) return;

    const description =
      (config.getDocumentDescription && config.getDocumentDescription()) ||
      (config.getDescription && config.getDescription()) ||
      matchingExpenseType;
    const itemsKey = lookupKeyToItemsKey[lookupKey];
    const lookupExpenses =
      itemsKey && data[itemsKey].length > 0
        ? mapExpensesByType(data, itemsKey, lookupKey, true)
        : medicalExpenses.filter((expense) => expense.claimTypeCode === claimCode);

    if (!documentSectionsByClaimCode.has(documentClaimCode)) {
      documentSectionsByClaimCode.set(documentClaimCode, {
        description,
        expenses: [],
        lookupKeys: [],
      });
    }
    const entry = documentSectionsByClaimCode.get(documentClaimCode);
    entry.expenses.push(...lookupExpenses);
    entry.lookupKeys.push(lookupKey);
  });

  documentSectionsByClaimCode.forEach((entry, documentClaimCode) => {
    const claimTypeItem =
      claimTypes.find((item) => item.claimCode === documentClaimCode) ||
      entry.lookupKeys
        .map((key) => claimTypes.find((item) => item.claimCode === getClaimCodeFromLookup(key)))
        .find(Boolean);
    if (claimTypeItem) {
      const benefit = mapClaimExpensesLevelItem(
        claimTypeItem,
        entry.description,
        entry.expenses
      );
      benefit.claimCode = documentClaimCode;
      list.push(benefit);
    }
  });

  return list;
}

function mapExpenseItem(item, itemDate, claimCode) {
  const selectedClaimForArray = (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor;
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountClaimed || !item.currency) {
    return null;
  }
  return {
    shortDescription: `${item.descriptionOfItem}`,
    dateOfExpense: item.dateOfExpenses,
    clientCost: item.amountClaimed,
    currency: item.currency,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
  };
}

function mapClaimExpensesLevelItem(claimType, description, expenses) {
  return {
    claimCode: claimType.claimCode,
    description,
    claimTypeId: claimType.claimTypeId,
    requiredDocuments: claimType.requiredDocuments,
    order: null,
    expenses: expenses || [],
  };
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
  mapToClaimExpensesLevel,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepStudentMedicalBenefitsTugoDataMapper.js